import React from 'react';
import { DocumentAiEnquirySummary, useAiDocumentChat, getDocumentAiDisplayState } from '../../../documentAi/AiDocumentChatContext';
import { makeStyles, createStyles } from '@material-ui/core';
import { AI_ACCENT } from '../../../documentAi/aiTheme';
import { LightTooltip } from '../components/LightTooltip';
import { AiProcessingIndicator } from '../../../documentAi/AiProcessingIndicator';
import { WandIcon } from '../../../components/icons/WandIcon';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { AnimatedItem } from '../../../components/animations/AnimatedItem';
import { SkeletonLoader } from '../../../components/loading/SkeletonLoader';

// Explicit flow change: the per-document AI button (against each document row) and the
// Documents panel's own "AI" button were both removed - this header badge, next to the Area of
// Law chip, is now the *only* entry point into AI processing, so there's only ever one AI
// progress/result to show per enquiry, never one per document. See AiDocumentChatContext.tsx's
// own comment on the matching removal of per-document session auto-discovery (the hydration
// query/global subscription that used to surface a minimised pill per document). Styled to
// match the header row's existing infoBadge/infoBadgeIcon/infoBadgeValue chips rather than
// looking like a button dropped into a badge row.
interface EnquiryAiHeaderBadgeProps {
    enquiryGuid: string;
    enquirySummary: DocumentAiEnquirySummary;
    badgeClassName: string;
    iconClassName: string;
    valueClassName: string;
}

// Explicit bug report: clicking felt "stuck"/unresponsive - requestAssessment's own fresh read is
// async, and (a) there was no immediate tactile feedback on the click itself, and (b) nothing
// distinguished "still checking" from "never assessed" while that read (or the mount-time
// ensureStatusChecked one) was in flight, so the badge kept showing the plain idle "AI" look
// right through it.
const useAiBadgeStyles = makeStyles(() =>
    createStyles({
        // AnimatedItem's wrapper is a block; this keeps the badge sitting in the badge row exactly as it did without one
        badgeSlot: { display: 'flex' },
        // Explicit request: make this stand out the same way the Check Duplicates button
        // does - a raised, pressable chip (white fill, layered shadow, lift on hover, flatten
        // on press) instead of the flat bordered pill the other header badges share. See
        // checkDuplicatesButton above for the matching values.
        pressable: {
            backgroundColor: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.1)',
            transition: 'box-shadow 0.15s ease, transform 0.15s ease',
            '&:hover': {
                boxShadow: '0 3px 8px rgba(0,0,0,0.16)',
                transform: 'translateY(-1px)',
            },
            '&:active': {
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                transform: 'translateY(0)',
            },
            '@media (prefers-reduced-motion: reduce)': {
                '&:hover': {
                    transform: 'none',
                },
            },
        },
    })
);

export const EnquiryAiHeaderBadge: React.FC<EnquiryAiHeaderBadgeProps> = ({
    enquiryGuid, enquirySummary, badgeClassName, iconClassName, valueClassName,
}) => {
    const badgeClasses = useAiBadgeStyles();
    const { getAssessmentSession, requestAssessment, ensureStatusChecked, isAssessmentChecking } = useAiDocumentChat();
    const session = getAssessmentSession(enquiryGuid);
    const displayState = getDocumentAiDisplayState(session);
    // Only meaningful while there's no session yet to show real state from instead - once a
    // session exists, its own processing/ready/failed state already covers the UI, so a later
    // requestAssessment click re-checking doesn't flash this back on top of it.
    const isChecking = !session && isAssessmentChecking(enquiryGuid);

    // Explicit request: opening an enquiry showed "AI", then "Checking…", then the real state in a few hundred
    // milliseconds - three looks for one badge. Until the first status check on opening has finished, a skeleton pill
    // (the same size as the badge, matching the placeholders next to it) holds the place, then the badge fades in. A
    // check the user starts by clicking still shows "Checking…" as before - that is feedback on their own click.
    const [firstCheckPhase, setFirstCheckPhase] = React.useState<'waiting' | 'checking' | 'done'>('waiting');
    React.useEffect(() => {
        setFirstCheckPhase('waiting');
    }, [enquiryGuid]);
    React.useEffect(() => {
        if (firstCheckPhase === 'waiting' && isChecking) {
            setFirstCheckPhase('checking');
        } else if (firstCheckPhase === 'checking' && !isChecking) {
            setFirstCheckPhase('done');
        }
    }, [firstCheckPhase, isChecking]);
    // never leave the skeleton up for good if the check somehow never reports back
    React.useEffect(() => {
        const timeoutId = window.setTimeout(() => setFirstCheckPhase('done'), 6000);
        return () => window.clearTimeout(timeoutId);
    }, [enquiryGuid]);
    const isFirstCheckPending = !session && firstCheckPhase !== 'done';

    // This badge only ever renders for an existing (saved) enquiry, so its own mount is a good
    // single place to hydrate AI status for the whole drawer - documents that were already
    // processing/already ready (a previous session, or an upload's own auto-queued processing)
    // then show correctly without the user needing to click anything first.
    //
    // Explicit bug report (round 1): clicking "Open enquiry" (inside the AI dialog itself) while
    // that same enquiry's drawer was already open re-fired GetEnquiryAIAssessment every single
    // time. Root cause - enquirySummary is built fresh (buildDocumentAiEnquirySummary(values)) at
    // this component's own call site on every render of that form, a brand new object reference
    // each time even when name/email/areaOfLaw haven't actually changed, and this effect's
    // dependency array compared enquirySummary by reference.
    //
    // Explicit bug report (round 2): saving/updating the enquiry ALSO re-fired the query, on top
    // of the terminal-subscription reload that already (correctly, separately) fires once a run
    // finishes - the previous round's own fix (comparing enquirySummary's actual field values
    // instead of its reference) still re-fired whenever name/email/areaOfLaw genuinely changed,
    // e.g. the form reinitialising from the server's own response after a save. But
    // ensureStatusChecked's whole purpose is "has the ENQUIRY ITSELF been opened" (AI assessment
    // status has nothing to do with the enquiry's name/email/area of law) - enquirySummary is
    // only ever display metadata, cached here for later, not something that should gate WHEN
    // status gets checked. Keying this purely on enquiryGuid fixes both rounds at once: it only
    // re-fires when a genuinely different (or newly mounted) enquiry is being viewed, never from
    // editing this same one's own fields. requestAssessment's own click handler still refreshes
    // enquirySummariesRef with the latest values on every explicit click regardless, so a stale
    // mount-time snapshot here is never a problem in practice.
    React.useEffect(() => {
        ensureStatusChecked(enquiryGuid, enquirySummary);
    // tslint:disable-next-line: align
    }, [enquiryGuid, ensureStatusChecked]);

    const tooltip = isChecking
        ? 'Checking AI assessment status…'
        : displayState === 'processing'
            ? 'Assessing this enquiry and its documents with AI...'
            : displayState === 'processingWithResults'
                ? 'Reassessing with AI - click to view the previous result'
                : displayState === 'ready'
                    ? 'AI assessment ready - click to view'
                    : displayState === 'failed'
                        ? `${session?.errorMessage || 'AI assessment failed.'} Click to retry.`
                        : 'Assess this enquiry and its documents with AI';

    const statusColor = displayState === 'ready' ? '#2e7d32' : displayState === 'failed' ? '#e53935' : AI_ACCENT;

    // Always goes through requestAssessment, regardless of displayState - it does its own fresh
    // read first (a "ready" badge can still be stale: something may have changed since this
    // session was last hydrated) and only dispatches assessEnquiryAI when that fresh read says
    // there's no assessment yet or needsAssessment is true, otherwise it just opens/shows
    // whatever's actually current. See AiDocumentChatContext.tsx's own comment on requestAssessment.
    const onClick = () => {
        requestAssessment(enquiryGuid, 'All documents', enquirySummary);
    };

    if (isFirstCheckPending) {
        return (
            <div className={badgeClassName} aria-busy={true}>
                <SkeletonLoader variant="shape" shape="rect" width={40} height={18} style={{ borderRadius: 999 }} />
            </div>
        );
    }

    return (
        <AnimatedItem noFill={true} className={badgeClasses.badgeSlot}>
            <LightTooltip arrow={true} title={tooltip}>
                <div
                    className={`${badgeClassName} ${badgeClasses.pressable}`}
                    style={{ cursor: 'pointer', borderColor: statusColor }}
                    onClick={onClick}
                >
                    {isChecking || displayState === 'processing' || displayState === 'processingWithResults' ? (
                        <AiProcessingIndicator size={15} />
                    ) : displayState === 'ready' ? (
                        <CheckCircleOutlineIcon className={iconClassName} style={{ color: statusColor }} />
                    ) : displayState === 'failed' ? (
                        <ErrorOutlineIcon className={iconClassName} style={{ color: statusColor }} />
                    ) : (
                        <WandIcon className={iconClassName} style={{ color: statusColor }} />
                    )}
                    <span className={valueClassName} style={{ color: statusColor }}>
                        {isChecking ? 'Checking…'
                            : displayState === 'processing' ? 'Assessing…'
                                : displayState === 'processingWithResults' ? 'Reassessing…'
                                    : displayState === 'ready' ? 'AI ready'
                                        : displayState === 'failed' ? 'Retry AI' : 'AI'}
                    </span>
                </div>
            </LightTooltip>
        </AnimatedItem>
    );
};

// Explicit bug report: the app was getting stuck. Root cause - AiDocumentChatContext.tsx's own
// context value is a fresh object literal every render (never memoized), and its `sessions`
// state updates on every single documentAIProgress/enquiryAIProgress subscription tick, which
// fire frequently while any AI processing is running anywhere. The main EnquiryFormDrawer
// component (10,000+ lines) used to call useAiDocumentChat() directly at its own top level just
// to read this one derived boolean - since MUI's Drawer keeps its children mounted even while
// closed, that meant the ENTIRE drawer's render function re-ran on every AI progress tick,
// app-wide, for the rest of the session once it had been opened once, regardless of which page
// was actually being viewed or whose enquiry was being processed.
//
// This tiny component is the fix: it's the one that actually subscribes to the AI context (so
// IT re-renders on every tick, which is cheap - one hook call, one boolean, renders null), and
// only calls onChange (the parent's setState) when the derived boolean actually flips. React's
// useState setter already bails out of re-rendering the parent when given the same value, so
// the massive drawer now only re-renders when the AI dialog's open/closed state truly changes -
// a rare event - instead of on every background progress tick.
export const AiChatDialogOpenWatcher: React.FC<{ onChange: (isOpen: boolean) => void }> = ({ onChange }) => {
    const { sessions, activeSessionKey } = useAiDocumentChat();
    const isOpen = sessions.some(session => session.key === activeSessionKey && !session.isMinimized);

    React.useEffect(() => {
        onChange(isOpen);
    // tslint:disable-next-line: align
    }, [isOpen, onChange]);

    return null;
};
